import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  buildConversationSearch,
  CONVERSATION_COMMENT_PARAM,
  CONVERSATION_MESSAGE_PARAM,
  CONVERSATION_PHRASE_PARAM,
  CONVERSATION_TAB_PARAM,
  type ConversationLinkQuery,
  type ConversationListItem,
} from '@/entities/conversations';
import { useDialogsSectionRoute } from '@/pages/dialogs/model/useDialogsSectionRoute';

/**
 * Переход в карточку диалога из списка. В том же окне «Назад» вернёт на список с его текущими
 * query-параметрами (единый контракт возврата с дашбордом); в новом окне открывается прямой адрес
 * карточки. Совпавшие при поиске по содержимому фрагменты (реплика звонка, сообщение переписки,
 * комментарий) прокидываются в query, чтобы карточка открылась на нужной вкладке и элементе.
 *
 * Запись открывается в том разделе, из которого её открыли: из раздела закреплённого пресета —
 * в его же адресе, из «Диалогов» — в адресе диалогов.
 */
export const useDialogRowClick = () => {
  const location = useLocation();
  const { listPath, buildRecordPath, navigateToRecord } = useDialogsSectionRoute();

  return useCallback(
    (item: ConversationListItem, openInNewWindow: boolean) => {
      const itemId = item.id?.toString();
      if (!itemId) return;

      const query: ConversationLinkQuery = {};
      if (item.matchedPhraseId != null) query[CONVERSATION_PHRASE_PARAM] = String(item.matchedPhraseId);
      if (item.matchedMessageId != null) query[CONVERSATION_MESSAGE_PARAM] = String(item.matchedMessageId);
      if (item.matchedCommentId != null) {
        query[CONVERSATION_TAB_PARAM] = 'comments';
        query[CONVERSATION_COMMENT_PARAM] = String(item.matchedCommentId);
      }

      if (openInNewWindow) {
        const basePath = buildRecordPath(itemId);
        const search = buildConversationSearch(query);
        window.open(search ? `${basePath}?${search}` : basePath, '_blank');
        return;
      }

      // Разделённый экран (лейаут one-page): переключение записи не покидает детализацию,
      // поэтому выбранное представление переносится на новую запись — активный таб из `?tab=`
      // и секция атрибутов из URL hash. Совпадение поиска по содержимому имеет приоритет:
      // оно уводит на собственный таб/комментарий.
      const currentTab = new URLSearchParams(location.search).get(CONVERSATION_TAB_PARAM);
      if (currentTab && query[CONVERSATION_TAB_PARAM] == null) query[CONVERSATION_TAB_PARAM] = currentTab;

      navigateToRecord(itemId, { state: `${listPath}${location.search}`, query, hash: location.hash });
    },
    [navigateToRecord, buildRecordPath, listPath, location],
  );
};
